import * as React from 'react';
import { cn } from '../../lib/utils';

export interface RadioOption {
  label: React.ReactNode;
  value: string | number;
}

export interface RadioGroupProps {
  label?: React.ReactNode;
  options: RadioOption[];
  name?: string;
  value?: string | number | null;
  onChange?: React.ChangeEventHandler<HTMLInputElement>;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  helper?: React.ReactNode;
  error?: string | boolean;
  disabled?: boolean;
  className?: string;
  withGap?: boolean;
}

const RadioGroup = React.forwardRef<HTMLDivElement, RadioGroupProps>(
  ({ label, options, name, value, onChange, onBlur, helper, error, disabled, className }, ref) => {
    const errorMessage = typeof error === 'string' ? error : undefined;
    return (
      <div ref={ref} className={cn('px-1', className)}>
        {label && <span className="mb-1 block text-sm font-medium text-foreground">{label}</span>}
        <div className="flex flex-col gap-1">
          {options.map((option) => (
            <label key={String(option.value)} className={cn('inline-flex items-center gap-2 cursor-pointer', disabled && 'opacity-50 cursor-not-allowed')}>
              <input
                type="radio"
                name={name}
                value={option.value}
                checked={value != null && String(value) === String(option.value)}
                onChange={onChange}
                onBlur={onBlur}
                disabled={disabled}
                className="h-4 w-4 border-input text-primary focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              />
              <span className="text-sm text-foreground">{option.label}</span>
            </label>
          ))}
        </div>
        {helper && <p className="mt-1 text-xs text-muted-foreground">{helper}</p>}
        {errorMessage && <p className="mt-1 text-xs text-destructive">{errorMessage}</p>}
      </div>
    );
  }
);
RadioGroup.displayName = 'RadioGroup';

export { RadioGroup };
